import { Mail } from 'lucide-react'
import { Input } from '@/components/atoms'
import { Checkbox } from '@/components/ui/checkbox'
import { INDUSTRIES_OF_INTEREST } from '@/constants'
import type { ProfileInterestsViewProps } from './ProfileInterestsViewProps'

export const ProfileInterestsDesktop = ({
  formData,
  updating,
  desktopBuyInput,
  desktopSellInput,
  isAddingDesktopBuy,
  isAddingDesktopSell,
  isUpdatingDesktopIndustries,
  selectedIndustries,
  industryLabelMap,
  onDesktopBuyInputChange,
  onDesktopSellInputChange,
  onAddBuyInterests,
  onAddSellInterests,
  onIndustryToggle,
  onSelectAllIndustries,
  onClearAllIndustries,
}: ProfileInterestsViewProps) => {
  const leftColumnIndustries = INDUSTRIES_OF_INTEREST.filter((_, index) => index % 2 === 0)
  const rightColumnIndustries = INDUSTRIES_OF_INTEREST.filter((_, index) => index % 2 === 1)

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-[788px_788px] gap-4">
        <div className="h-[213px] border border-white bg-[rgba(255,255,255,0.32)] px-8 py-8 dark:border-[#171717] dark:bg-[rgba(23,23,23,0.72)]">
          <p className="font-plus-jakarta text-[20px] font-semibold leading-[24px] tracking-[-0.2px] text-[#2d2923] dark:text-[#fdfdfd]">
            Assets You Want to Buy
          </p>

          <div className="mt-6 flex items-center gap-3">
            <Input
              type="text"
              value={desktopBuyInput}
              onChange={(e) => onDesktopBuyInputChange(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  void onAddBuyInterests()
                }
              }}
              placeholder="e.g. Anduril, SpaceX"
              disabled={updating || isAddingDesktopBuy}
              leftElement={<Mail className="h-5 w-5 text-[rgba(45,41,35,0.64)] dark:text-[rgba(253,253,253,0.64)]" />}
              className="!h-12 !w-[320px] !rounded-none !border-[rgba(45,41,35,0.12)] !bg-[rgba(255,255,255,0.52)] !py-[14px] !pr-[18px] !pl-[42px] font-plus-jakarta !text-[14px] !font-medium !leading-[20px] !text-[rgba(45,41,35,0.72)] placeholder:!text-[rgba(45,41,35,0.72)] dark:!border-[rgba(253,253,253,0.12)] dark:!bg-[#1c1c1c] dark:!text-[rgba(253,253,253,0.72)] dark:placeholder:!text-[rgba(253,253,253,0.72)]"
            />

            <button
              type="button"
              onClick={() => {
                void onAddBuyInterests()
              }}
              disabled={!desktopBuyInput.trim() || updating || isAddingDesktopBuy}
              className="h-12 border border-[#2d2923] bg-[rgba(255,255,255,0.72)] px-4 font-plus-jakarta text-[14px] font-medium leading-[20px] text-[#2d2923] transition-opacity disabled:cursor-not-allowed disabled:opacity-50 dark:border-[#fdfdfd] dark:bg-[rgba(45,41,35,0.72)] dark:text-[#fdfdfd]"
            >
              Add
            </button>
          </div>

          <div className="mt-[17px] flex flex-wrap items-center gap-[10px]">
            {(formData.companies_of_interest_buy || []).map((item) => (
              <span
                key={item}
                className="inline-flex h-[30px] min-w-[140px] items-center justify-center border border-[#1fb845] bg-white px-2 py-[2px] text-center font-plus-jakarta text-[12px] font-semibold uppercase leading-[16px] text-[#1fb845]"
              >
                {item}
              </span>
            ))}
          </div>
        </div>

        <div className="h-[213px] border border-white bg-[rgba(255,255,255,0.32)] px-8 py-8 dark:border-[#171717] dark:bg-[rgba(23,23,23,0.72)]">
          <p className="font-plus-jakarta text-[20px] font-semibold leading-[24px] tracking-[-0.2px] text-[#2d2923] dark:text-[#fdfdfd]">
            Assets You Want to Sell
          </p>

          <div className="mt-6 flex items-center gap-3">
            <Input
              type="text"
              value={desktopSellInput}
              onChange={(e) => onDesktopSellInputChange(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  void onAddSellInterests()
                }
              }}
              placeholder="e.g. Anduril, Palantir"
              disabled={updating || isAddingDesktopSell}
              leftElement={<Mail className="h-5 w-5 text-[rgba(45,41,35,0.64)] dark:text-[rgba(253,253,253,0.64)]" />}
              className="!h-12 !w-[320px] !rounded-none !border-[rgba(45,41,35,0.12)] !bg-[rgba(255,255,255,0.52)] !py-[14px] !pr-[18px] !pl-[42px] font-plus-jakarta !text-[14px] !font-medium !leading-[20px] !text-[rgba(45,41,35,0.72)] placeholder:!text-[rgba(45,41,35,0.72)] dark:!border-[rgba(253,253,253,0.12)] dark:!bg-[#1c1c1c] dark:!text-[rgba(253,253,253,0.72)] dark:placeholder:!text-[rgba(253,253,253,0.72)]"
            />

            <button
              type="button"
              onClick={() => {
                void onAddSellInterests()
              }}
              disabled={!desktopSellInput.trim() || updating || isAddingDesktopSell}
              className="h-12 border border-[#2d2923] bg-[rgba(255,255,255,0.72)] px-4 font-plus-jakarta text-[14px] font-medium leading-[20px] text-[#2d2923] transition-opacity disabled:cursor-not-allowed disabled:opacity-50 dark:border-[#fdfdfd] dark:bg-[rgba(45,41,35,0.72)] dark:text-[#fdfdfd]"
            >
              Add
            </button>
          </div>

          <div className="mt-[17px] flex flex-wrap items-center gap-[10px]">
            {(formData.companies_of_interest_sell || []).map((item) => (
              <span
                key={item}
                className="inline-flex h-[30px] min-w-[92px] items-center justify-center border border-[#ff540c] bg-white px-2 py-[2px] text-center font-plus-jakarta text-[12px] font-semibold uppercase leading-[16px] text-[#ff540c]"
              >
                {item}
              </span>
            ))}
          </div>
        </div>
      </div>

      <div className="h-[412px] w-[788px] border border-white bg-[rgba(255,255,255,0.32)] px-8 py-8 dark:border-[#171717] dark:bg-[rgba(23,23,23,0.72)]">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h3 className="font-plus-jakarta text-[20px] font-semibold leading-[24px] tracking-[-0.2px] text-[#2d2923] dark:text-[#fdfdfd]">
              Industries of Interest
            </h3>
            <p className="font-plus-jakarta text-[12px] font-normal leading-[16px] text-[rgba(45,41,35,0.64)] dark:text-[rgba(253,253,253,0.64)]">
              Selected: {selectedIndustries.length} of {INDUSTRIES_OF_INTEREST.length}
            </p>
          </div>

          <div className="flex items-center gap-4">
            <button
              type="button"
              onClick={() => {
                void onSelectAllIndustries()
              }}
              disabled={updating || isUpdatingDesktopIndustries}
              className="font-plus-jakarta text-[14px] font-medium leading-[20px] text-[#2d2923] underline disabled:cursor-not-allowed disabled:opacity-50 dark:text-[#fdfdfd]"
            >
              Select All
            </button>
            <button
              type="button"
              onClick={() => {
                void onClearAllIndustries()
              }}
              disabled={updating || isUpdatingDesktopIndustries}
              className="font-plus-jakarta text-[14px] font-medium leading-[20px] text-[#2d2923] underline disabled:cursor-not-allowed disabled:opacity-50 dark:text-[#fdfdfd]"
            >
              Clear All
            </button>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-2 gap-x-16">
          <div className="space-y-4">
            {leftColumnIndustries.map((industry) => (
              <label key={industry} className="flex cursor-pointer items-center gap-3">
                <Checkbox
                  checked={selectedIndustries.includes(industry)}
                  onCheckedChange={(checked) => {
                    void onIndustryToggle(industry, checked === true)
                  }}
                  disabled={updating || isUpdatingDesktopIndustries}
                  className="rounded-none dark:border-[rgba(253,253,253,0.32)] dark:bg-[rgba(28,28,28,0.56)]"
                />
                <span className="font-plus-jakarta text-[14px] font-medium leading-[20px] text-[#2d2923] dark:text-[#fdfdfd]">
                  {industryLabelMap[industry] || industry}
                </span>
              </label>
            ))}
          </div>

          <div className="space-y-4">
            {rightColumnIndustries.map((industry) => (
              <label key={industry} className="flex cursor-pointer items-center gap-3">
                <Checkbox
                  checked={selectedIndustries.includes(industry)}
                  onCheckedChange={(checked) => {
                    void onIndustryToggle(industry, checked === true)
                  }}
                  disabled={updating || isUpdatingDesktopIndustries}
                  className="rounded-none dark:border-[rgba(253,253,253,0.32)] dark:bg-[rgba(28,28,28,0.56)]"
                />
                <span className="font-plus-jakarta text-[14px] font-medium leading-[20px] text-[#2d2923] dark:text-[#fdfdfd]">
                  {industryLabelMap[industry] || industry}
                </span>
              </label>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
